import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { FeatureCard, CardTitle } from '../components/ui';
import { fetchNewsIndex, CategoryChip, NewsThumb, formatNewsDate } from '../news';

const SITE = 'https://qasc.it';
const PAGE_SIZE = 12;

function NewsPage() {
  const [items, setItems] = useState(null);
  const [shown, setShown] = useState(PAGE_SIZE);

  useEffect(() => {
    let alive = true;
    fetchNewsIndex().then((data) => {
      if (alive) setItems(data);
    });
    return () => {
      alive = false;
    };
  }, []);

  const visible = useMemo(() => (items || []).slice(0, shown), [items, shown]);

  return (
    <PageLayout
      seo={{
        title: 'News e Pillole di Sicurezza | QAS Consulting',
        description:
          'News e aggiornamenti normativi su sicurezza sul lavoro, ambiente e qualità: le pillole di sicurezza di QAS Consulting.',
        canonical: `${SITE}/news`,
      }}
      hero={{
        kicker: 'News & Aggiornamenti',
        title: 'News e pillole di sicurezza',
        subtitle: 'Gli aggiornamenti normativi su sicurezza sul lavoro, ambiente e qualità.',
      }}
    >
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="L'archivio"
            title="Le ultime dal mondo QAS"
            subtitle="Per ricevere gli aggiornamenti direttamente nella tua casella di posta, iscriviti alla newsletter in fondo alla pagina."
          />
          {!items ? (
            <Typography sx={{ textAlign: 'center', color: '#48606d' }}>
              Caricamento delle news…
            </Typography>
          ) : (
            <>
              <Grid container spacing={3}>
                {visible.map((n) => (
                  <Grid item xs={12} sm={6} md={4} key={n.slug}>
                    <FeatureCard
                      component={RouterLink}
                      to={`/news/${n.slug}`}
                      sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        textDecoration: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <NewsThumb image={n.image} title={n.title} />
                      <Box sx={{ p: '22px 24px 26px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '13px', color: '#7a8f9a' }}>
                            {formatNewsDate(n.date)}
                          </Typography>
                          {(n.categories || []).slice(0, 1).map((c) => (
                            <CategoryChip key={c} label={c} />
                          ))}
                        </Box>
                        <CardTitle sx={{ fontSize: '15.5px', lineHeight: 1.5, mb: 1 }}>
                          {n.title}
                        </CardTitle>
                        <Typography sx={{ fontSize: '13.5px', lineHeight: 1.65, color: '#48606d' }}>
                          {n.excerpt && n.excerpt.length > 120 ? `${n.excerpt.slice(0, 119)}…` : n.excerpt}
                        </Typography>
                      </Box>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
              {shown < items.length && (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Typography
                    component="button"
                    onClick={() => setShown((s) => s + PAGE_SIZE)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontFamily: '"Sora", sans-serif',
                      backgroundColor: 'primary.main',
                      color: '#ffffff',
                      px: '30px',
                      py: '13px',
                      borderRadius: '999px',
                      fontWeight: 600,
                      fontSize: '15px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { backgroundColor: 'primary.dark', transform: 'translateY(-2px)' },
                    }}
                  >
                    Carica altre news ({items.length - shown})
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Container>
      </Section>
    </PageLayout>
  );
}

export default NewsPage;
